/**
 * DENIAL → HEBREW. 09 §5.8.
 *
 * "An external customer must never see a raw 403, a raw 404, a stack trace, an English exception,
 * or a blank screen. Every state is a written Hebrew screen with a title, one sentence of
 * explanation, and one obvious next step."
 *
 * Two layers, from `packages/contracts/src/decision.ts` (`DenyDecision.disclosable` / `toDenyBody`):
 *
 *   inside the tenant  → the first failing stage's reason, named, with a next step.
 *   outside the tenant → `reason` never arrives. One uniform sentence, revealing nothing —
 *                        not whether the object exists, not which stage failed, not in which tenant.
 *
 * This module is pure. It maps codes to words. It grants nothing and it checks nothing.
 */

import type { DenyReason } from './contract';

/** Where the next step points. The shell turns this into a route or a control. */
export type DenialActionKind =
  | 'sign_in'
  | 'back_to_companies'
  | 'contact_office_manager'
  | 'request_access'
  | 'contact_support'
  | 'retry'
  | 'verify_identity'
  | 'none';

export interface DenialText {
  /** A noun phrase in office Hebrew (09 §9.1 V4). */
  readonly titleHe: string;
  /** Exactly one sentence. No route, no path, no exception type, no status code (09 §9.1 V3). */
  readonly explanationHe: string;
  /** Exactly one next step. There is no denial with zero next steps. */
  readonly nextStepHe: string;
  readonly action: DenialActionKind;
  /**
   * `blocked` = the office/subscription must act. `permission` = a person must be granted.
   * `identity` = the user must re-establish who they are. Used for chrome, never for logic.
   */
  readonly tone: 'blocked' | 'permission' | 'identity' | 'temporary';
}

/**
 * The uniform, non-disclosing text. Used whenever `reason` is absent — which is exactly the case
 * `toDenyBody` produces outside the tenant. It must stay identical for every reason, because the
 * whole point is that the caller cannot tell them apart (S-12).
 */
export const UNIFORM_DENIAL: DenialText = {
  titleHe: 'אין גישה',
  explanationHe: 'אין באפשרותך לצפות בדף הזה.',
  nextStepHe: 'חזרה לחברות שלי',
  action: 'back_to_companies',
  tone: 'permission',
};

const CONTACT_OFFICE = 'פנייה למנהל הארגון';

/**
 * Every member of `DENY_REASONS` gets a row. `contract.mirror.test.ts` fails the build if
 * `packages/contracts/src/decision.ts` grows a code that is missing here — a denial with no Hebrew
 * is a raw 403 with extra steps.
 *
 * 09 §5.8 / CONFLICT C-4: "not subscribed" and "no permission" are deliberately distinguishable
 * INSIDE the tenant. Outside it they collapse into `UNIFORM_DENIAL`.
 * O-3: no pricing and no upgrade button in a "not subscribed" message.
 */
const DENIAL_TEXT: Readonly<Record<DenyReason, DenialText>> = {
  // ── ROUTE ───────────────────────────────────────────────────────────────────────────────────────
  declaration_missing: {
    titleHe: 'הדף הזה אינו זמין',
    explanationHe: 'הדף שביקשת אינו מוגדר במערכת.',
    nextStepHe: 'חזרה לדף הבית של החברה',
    action: 'back_to_companies',
    tone: 'temporary',
  },

  // ── AUTHN ───────────────────────────────────────────────────────────────────────────────────────
  no_session: {
    titleHe: 'נדרשת התחברות',
    explanationHe: 'כדי להמשיך יש להתחבר למערכת.',
    nextStepHe: 'התחברות',
    action: 'sign_in',
    tone: 'identity',
  },
  session_expired: {
    titleHe: 'ההתחברות פגה',
    explanationHe: 'עברו יותר מדי זמן מאז ההתחברות האחרונה שלך.',
    nextStepHe: 'התחברות מחדש',
    action: 'sign_in',
    tone: 'identity',
  },
  session_revoked: {
    titleHe: 'ההתחברות בוטלה',
    explanationHe: 'ההתחברות שלך בוטלה על ידי מנהל הארגון.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'identity',
  },
  anonymous_not_permitted: {
    titleHe: 'נדרשת התחברות',
    explanationHe: 'הדף הזה פתוח רק למשתמשים מזוהים.',
    nextStepHe: 'התחברות',
    action: 'sign_in',
    tone: 'identity',
  },
  session_context_mismatch: {
    // OA-0001 §2.1. A user should never see this: it means a session claims a context its account
    // is not entitled to, which the schema makes uninsertable. If it appears, the session is
    // corrupt rather than the user unauthorised — so the wording sends them to sign in again and
    // does not suggest they lack permission.
    titleHe: 'ההתחברות אינה תקינה',
    explanationHe: 'ההתחברות שלך אינה תואמת את סוג החשבון. נדרשת התחברות מחדש.',
    nextStepHe: 'התחברות מחדש',
    action: 'sign_in',
    tone: 'identity',
  },
  stepup_required: {
    titleHe: 'נדרש אימות נוסף',
    explanationHe: 'הפעולה הזו רגישה ודורשת אימות זהות נוסף לפני שתתבצע.',
    nextStepHe: 'אימות זהות והמשך',
    action: 'verify_identity',
    tone: 'identity',
  },

  // ── TENANT / context ───────────────────────────────────────────────────────────────────────────
  no_tenant_context: {
    titleHe: 'לא נבחר ארגון',
    explanationHe: 'הדף הזה שייך לארגון מסוים, ולא נבחר ארגון פעיל.',
    nextStepHe: 'בחירת ארגון',
    action: 'back_to_companies',
    tone: 'blocked',
  },
  wrong_context: {
    titleHe: 'הדף הזה שייך לסביבה אחרת',
    explanationHe: 'הדף שביקשת אינו קיים בסביבה שבה אתה נמצא כעת.',
    nextStepHe: 'חזרה לחברות שלי',
    action: 'back_to_companies',
    tone: 'blocked',
  },
  platform_context_no_grant: {
    titleHe: 'אין הרשאת תמיכה לארגון הזה',
    explanationHe: 'כניסה לנתוני ארגון מחייבת הרשאת תמיכה פעילה ומתועדת.',
    nextStepHe: 'בקשת הרשאת תמיכה',
    action: 'request_access',
    tone: 'permission',
  },
  cross_tenant: {
    titleHe: 'אין גישה',
    explanationHe: 'אין באפשרותך לצפות בדף הזה.',
    nextStepHe: 'חזרה לחברות שלי',
    action: 'back_to_companies',
    tone: 'permission',
  },

  // ── SUBSCRIPTION ───────────────────────────────────────────────────────────────────────────────
  subscription_inactive: {
    titleHe: 'המנוי של הארגון אינו פעיל',
    explanationHe: 'המנוי של הארגון אינו פעיל, ולכן החלק הזה סגור כרגע.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  subscription_trialing_restricted: {
    titleHe: 'החלק הזה אינו כלול בתקופת הניסיון',
    explanationHe: 'הארגון נמצא בתקופת ניסיון, והחלק הזה אינו נכלל בה.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  subscription_past_due: {
    titleHe: 'קיים חוב פתוח במנוי הארגון',
    explanationHe: 'במנוי של הארגון קיים תשלום שטרם הוסדר.',
    nextStepHe: 'פנייה למנהל הארגון להסדרת התשלום',
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  subscription_suspended: {
    titleHe: 'המנוי של הארגון מושהה',
    explanationHe: 'המנוי של הארגון מושהה, ולכן המערכת סגורה לצפייה ולעדכון.',
    nextStepHe: 'פנייה למנהל הארגון להפעלת המנוי',
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  subscription_expired: {
    titleHe: 'המנוי של הארגון הסתיים',
    explanationHe: 'תוקף המנוי של הארגון הסתיים.',
    nextStepHe: 'פנייה למנהל הארגון לחידוש המנוי',
    action: 'contact_office_manager',
    tone: 'blocked',
  },

  // ── COMPANY ────────────────────────────────────────────────────────────────────────────────────
  no_company_grant: {
    titleHe: 'אין לך גישה לחברה הזו',
    explanationHe: 'החברה הזו קיימת בארגון, אך לא ניתנה לך גישה אליה.',
    nextStepHe: 'בקשת גישה ממנהל הארגון',
    action: 'request_access',
    tone: 'permission',
  },
  company_not_resolvable: {
    titleHe: 'לא ניתן לזהות את החברה',
    explanationHe: 'לא הצלחנו לזהות את החברה שאליה מפנה הקישור.',
    nextStepHe: 'חזרה לחברות שלי',
    action: 'back_to_companies',
    tone: 'blocked',
  },
  company_suspended: {
    titleHe: 'החברה מושהית',
    explanationHe: 'החברה הזו מושהית ולא ניתן לעבוד בה כרגע.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  company_archived: {
    titleHe: 'החברה בארכיון',
    explanationHe: 'החברה הזו הועברה לארכיון וניתן רק לצפות בנתוני העבר שלה.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  company_review_required: {
    titleHe: 'החברה ממתינה לבדיקה',
    explanationHe: 'פרטי החברה ממתינים לבדיקה בארגון, ולכן לא ניתן לעדכן בה נתונים.',
    nextStepHe: 'פנייה למנהל הארגון להשלמת הבדיקה',
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  company_inactive: {
    titleHe: 'החברה אינה פעילה',
    explanationHe: 'החברה הזו מסומנת כלא פעילה בארגון.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },

  // ── MODULE ─────────────────────────────────────────────────────────────────────────────────────
  not_subscribed: {
    titleHe: 'המודול הזה לא נכלל במנוי של הארגון',
    explanationHe: 'הארגון לא רכש את החלק הזה של המערכת.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  sku_expired: {
    titleHe: 'תוקף המודול הסתיים',
    explanationHe: 'תוקף הרכישה של החלק הזה במנוי הארגון הסתיים.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  unknown_module: {
    titleHe: 'המודול הזה אינו קיים',
    explanationHe: 'החלק שביקשת אינו קיים במערכת.',
    nextStepHe: 'חזרה לדף הבית של החברה',
    action: 'back_to_companies',
    tone: 'blocked',
  },
  missing_module: {
    titleHe: 'המודול הזה אינו זמין',
    explanationHe: 'החלק הזה אינו זמין בארגון שלך.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  company_not_assigned: {
    titleHe: 'החברה הזו לא שויכה למודול',
    explanationHe: 'הארגון רכש את החלק הזה, אך החברה הזו לא שויכה אליו.',
    nextStepHe: 'פנייה למנהל הארגון לשיוך החברה',
    action: 'contact_office_manager',
    tone: 'blocked',
  },
  limit_reached: {
    titleHe: 'נוצלה מלוא המכסה במנוי',
    explanationHe: 'הארגון הגיע למכסה שנקבעה במנוי עבור החלק הזה.',
    nextStepHe: 'פנייה למנהל הארגון להרחבת המכסה',
    action: 'contact_office_manager',
    tone: 'blocked',
  },

  // ── CAPABILITY ─────────────────────────────────────────────────────────────────────────────────
  missing_capability: {
    titleHe: 'אין לך הרשאה לפעולה הזו',
    explanationHe: 'הפעולה הזו פתוחה רק למשתמשים שקיבלו עליה הרשאה מפורשת.',
    nextStepHe: 'בקשת הרשאה ממנהל הארגון',
    action: 'request_access',
    tone: 'permission',
  },
  capability_wrong_context: {
    titleHe: 'ההרשאה שלך אינה תקפה כאן',
    explanationHe: 'ההרשאה שקיבלת שייכת לסביבה אחרת ואינה חלה על הדף הזה.',
    nextStepHe: CONTACT_OFFICE,
    action: 'contact_office_manager',
    tone: 'permission',
  },

  // ── CSRF · RATE ────────────────────────────────────────────────────────────────────────────────
  csrf_failed: {
    titleHe: 'הפעולה לא בוצעה',
    explanationHe: 'הבקשה לא זוהתה כבקשה שנשלחה מהמסך הזה, ולכן לא בוצעה.',
    nextStepHe: 'רענון הדף וניסיון חוזר',
    action: 'retry',
    tone: 'temporary',
  },
  origin_not_allowed: {
    titleHe: 'הפעולה לא בוצעה',
    explanationHe: 'הבקשה הגיעה ממקור שאינו מורשה.',
    nextStepHe: 'רענון הדף וניסיון חוזר',
    action: 'retry',
    tone: 'temporary',
  },
  rate_limited: {
    titleHe: 'יותר מדי בקשות',
    explanationHe: 'נשלחו יותר מדי בקשות בזמן קצר.',
    nextStepHe: 'המתנה של דקה וניסיון חוזר',
    action: 'retry',
    tone: 'temporary',
  },

  // ── universal fail-closed ──────────────────────────────────────────────────────────────────────
  predicate_error: {
    titleHe: 'אירעה תקלה',
    explanationHe: 'לא הצלחנו לבדוק את ההרשאה לדף הזה, ולכן הוא נסגר עד לבירור.',
    nextStepHe: 'פתיחת פנייה לתמיכה עם קוד השגיאה',
    action: 'contact_support',
    tone: 'temporary',
  },
  indeterminate: {
    titleHe: 'אירעה תקלה',
    explanationHe: 'לא הצלחנו להכריע אם מותר להציג את הדף הזה.',
    nextStepHe: 'פתיחת פנייה לתמיכה עם קוד השגיאה',
    action: 'contact_support',
    tone: 'temporary',
  },
};

/**
 * The one mapping function.
 *
 * `reason === undefined` is not a bug and not a fallback — it is the contract outside the tenant.
 * An unrecognised code also collapses to `UNIFORM_DENIAL`: the UI fails closed and silent, never
 * with the raw code on screen (09 §9.1 V3).
 */
export function denialTextFor(reason: DenyReason | undefined): DenialText {
  if (reason === undefined) return UNIFORM_DENIAL;
  const text = DENIAL_TEXT[reason];
  return text ?? UNIFORM_DENIAL;
}

/** Exposed for the mirror test only. */
export function mappedDenyReasons(): readonly string[] {
  return Object.keys(DENIAL_TEXT);
}
